/**NIRSALCarbonValueBridge — Green Credit + Carbon + Plastic Value Bridge */
import React, { useState } from "react";
import { useNIRSAL } from "@/context/NIRSALContext";
import { PageHeader, SectionCard } from "@/components/ds";

const fmtU = (v) => { if (!v) return "—"; const n = Number(v); if (n>=1e6) return `$${(n/1e6).toFixed(1)}M`; if (n>=1000) return `$${(n/1000).toFixed(0)}K`; return `$${n}`; };
const fmtN = (v, u="") => { if (!v) return "—"; const n=Number(v); if (n>=1e6) return `${(n/1e6).toFixed(1)}M ${u}`.trim(); if (n>=1000) return `${(n/1000).toFixed(0)}K ${u}`.trim(); return `${n} ${u}`.trim(); };

export default function NIRSALCarbonValueBridge() {
  const { vbCache, selectedPeriod, loading } = useNIRSAL();
  const [filter, setFilter] = useState("all");
  const vb = vbCache[selectedPeriod] || {};
  const p = vb.portfolio || {};
  const opps = vb.opportunities || [];

  const filtered = filter === "all" ? opps : opps.filter(o => o.pathway === filter || (filter === "art6" && o.art6_eligible) || (filter === "vcm" && o.vcm_eligible && !o.art6_eligible) || (filter === "plastic" && o.is_plastic));

  if (loading) return <div className="p-8 text-slate-400 text-sm">Loading value bridge…</div>;

  return (
    <div className="p-6 lg:p-8 bg-[#F8FAFC] min-h-screen space-y-6">
      <PageHeader label="CARBON VALUE BRIDGE" title="" />
      <p className="text-xs text-slate-400 -mt-4">Green credit value · Carbon value · Plastic value · Capital mobilization — all clearly separated</p>

      {/* Three-value summary */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-[#0f2d5c] rounded-xl p-5 text-white">
          <p className="text-xs font-bold uppercase tracking-wider opacity-60 mb-2">Section A — Operational Value</p>
          <p className="text-2xl font-black">{fmtU(p.total_capital_mobilized)}</p>
          <p className="text-xs opacity-70 mt-1">Capital mobilized through CRG-backed green lending</p>
          <div className="mt-3 text-xs opacity-80">
            <div className="flex justify-between py-1 border-t border-white/10"><span>CRG Exposure</span><span>{fmtU(p.total_crg_exposure)}</span></div>
            <div className="flex justify-between py-1 border-t border-white/10"><span>Leverage Ratio</span><span>{(p.leverage_ratio||0).toFixed(1)}x</span></div>
          </div>
          <p className="text-[10px] opacity-50 mt-3">Financial value from financed climate assets — high confidence</p>
        </div>
        <div className="bg-emerald-700 rounded-xl p-5 text-white">
          <p className="text-xs font-bold uppercase tracking-wider opacity-60 mb-2">Section B — Indicative Carbon Value</p>
          <p className="text-2xl font-black">{fmtU(p.carbon_value_base_usd)}<span className="text-sm font-normal opacity-70">/yr</span></p>
          <p className="text-xs opacity-70 mt-1">Conditional on VVB validation and registry issuance</p>
          <div className="mt-3 text-xs opacity-80">
            <div className="flex justify-between py-1 border-t border-white/10"><span>Low ($10/t)</span><span>{fmtU(vb.carbon_scenarios?.low)}/yr</span></div>
            <div className="flex justify-between py-1 border-t border-white/10"><span>Base ($20/t)</span><span>{fmtU(vb.carbon_scenarios?.base)}/yr</span></div>
            <div className="flex justify-between py-1 border-t border-white/10"><span>High ($40/t)</span><span>{fmtU(vb.carbon_scenarios?.high)}/yr</span></div>
          </div>
          <p className="text-[10px] opacity-50 mt-3">Must NOT be included in base DSCR until validated and issued</p>
        </div>
        <div className="bg-purple-700 rounded-xl p-5 text-white">
          <p className="text-xs font-bold uppercase tracking-wider opacity-60 mb-2">Section C — Indicative Plastic Value</p>
          <p className="text-2xl font-black">{fmtU(vb.plastic_value || p.plastic_value_usd)}<span className="text-sm font-normal opacity-70">/yr</span></p>
          <p className="text-xs opacity-70 mt-1">Plastic credits at $150/tonne recovered plastic</p>
          <div className="mt-3 text-xs opacity-80">
            <div className="flex justify-between py-1 border-t border-white/10"><span>Plastic Recovered</span><span>{fmtN(p.total_plastic_recovered_t_yr,"t/yr")}</span></div>
            <div className="flex justify-between py-1 border-t border-white/10"><span>Standard</span><span>Verra Plastic Waste</span></div>
          </div>
          <p className="text-[10px] opacity-50 mt-3">Conditional — subject to plastic credit registry and verification</p>
        </div>
      </div>

      {/* Filters + Table */}
      <SectionCard title="Priority Clusters by Value & Readiness">
        <div className="px-4 pt-3 flex gap-2 flex-wrap">
          {[["all","All Clusters"],["art6","Art.6.4"],["vcm","VCM"],["plastic","Plastic"]].map(([v,l]) => (
            <button key={v} onClick={() => setFilter(v)}
              className={`text-xs px-3 py-1 rounded-full border transition-all ${filter === v ? "bg-[#1e3a5f] text-white border-[#1e3a5f]" : "bg-white text-slate-500 border-slate-200"}`}>{l}</button>
          ))}
          <span className="text-xs text-slate-400 ml-auto self-center">{filtered.length} of {opps.length} clusters</span>
        </div>
        <div className="overflow-x-auto p-2">
          <table className="w-full text-xs mt-2">
            <thead><tr className="border-b border-slate-100">
              <th className="text-left py-2 px-3 text-slate-400 font-medium">#</th>
              <th className="text-left py-2 px-3 text-slate-400 font-medium">Cluster</th>
              <th className="text-left py-2 px-3 text-slate-400 font-medium">State</th>
              <th className="text-right py-2 px-3 text-slate-400 font-medium">Reduction</th>
              <th className="text-right py-2 px-3 text-slate-400 font-medium">Carbon (Base)</th>
              <th className="text-right py-2 px-3 text-slate-400 font-medium">Capital</th>
              <th className="text-left py-2 px-3 text-slate-400 font-medium">Pathway</th>
              <th className="text-right py-2 px-3 text-slate-400 font-medium">Score</th>
            </tr></thead>
            <tbody>
              {filtered.map((o, i) => (
                <tr key={o.opp_id} className="border-b border-slate-50 hover:bg-slate-50">
                  <td className="py-2 px-3 text-slate-400">{i+1}</td>
                  <td className="py-2 px-3 font-medium max-w-[180px] truncate">{o.name}</td>
                  <td className="py-2 px-3 text-slate-500">{o.state}</td>
                  <td className="py-2 px-3 text-right text-emerald-700 font-bold">{fmtN(o.reduction_tco2e_yr,"tCO₂e/yr")}</td>
                  <td className="py-2 px-3 text-right">{fmtU(o.carbon_rev_base_usd_yr)}/yr</td>
                  <td className="py-2 px-3 text-right">{fmtU(o.capital_mobilized)}</td>
                  <td className="py-2 px-3"><span className="px-1.5 py-0.5 rounded text-[10px] font-bold bg-blue-50 text-blue-700">{o.pathway_label}</span></td>
                  <td className="py-2 px-3 text-right font-black text-[#1e3a5f]">{(o.composite_score||0).toFixed(0)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </SectionCard>
    </div>
  );
}
